import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBell,
  IconChartBar,
  IconDeviceGamepad2,
  IconMicrophone2,
  IconPlus,
  IconUsers,
  IconUsersGroup,
  IconWorld,
} from "@tabler/icons-react";
import { hasAtLeast } from "@wwrp/shared";
import type { Bulletin } from "@wwrp/shared";
import { dashboardApi } from "../api/dashboard.js";
import { membersApi } from "../api/members.js";
import { bulletinsApi } from "../api/bulletins.js";
import { useAuth } from "../contexts/AuthContext.js";
import { getAccessRole } from "../api/auth.js";
import { useRoleColors, FALLBACK_ROLE_COLOR } from "../components/RoleBadge.js";
import { BulletinCard, BulletinFormModal } from "../components/BulletinCard.js";

// ── Page ──────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const accessRole = getAccessRole(user);
  const isAdmin = accessRole ? hasAtLeast(accessRole, "administration") : false;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dashboardApi.get(),
  });

  const { data: onlineMembers = [], isLoading: onlineLoading } = useQuery({
    queryKey: ["members", "online"],
    queryFn: () => membersApi.listOnline(),
    refetchInterval: 30_000,
  });

  const {
    data: bulletins = [],
    isLoading: bulletinsLoading,
    isError: bulletinsError,
  } = useQuery({
    queryKey: ["bulletins"],
    queryFn: () => bulletinsApi.list(),
    refetchInterval: 60_000,
  });

  const roleColors = useRoleColors();

  // Only show the bulletin section if there are active bulletins or the user
  // is an admin who might want to create them.
  const now = Date.now();
  const activeBulletins = bulletins.filter(
    (b) => !b.expiresAt || new Date(b.expiresAt).getTime() > now,
  );
  const expiredBulletins = bulletins.filter(
    (b) => b.expiresAt && new Date(b.expiresAt).getTime() <= now,
  );
  const showSection = isAdmin || activeBulletins.length > 0;

  const [editingBulletin, setEditingBulletin] = useState<Bulletin | null>(null);
  const [creatingBulletin, setCreatingBulletin] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["bulletins"] });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bulletinsApi.remove(id),
    onSuccess: () => {
      notifications.show({ message: "Bulletin deleted.", color: "gray" });
      invalidate();
    },
    onError: (e: Error) => notifications.show({ title: "Error", message: e.message, color: "red" }),
  });

  return (
    <div className="page">
      <Stack className="page-header" gap={4}>
        <Title order={1} className="page-title">
          Dashboard
        </Title>
        <Text c="dimmed" size="sm">
          Community health, live activity, and who is online right now.
        </Text>
      </Stack>

      {isError && (
        <Alert color="red" mb="md">
          Failed to load dashboard data.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="lg">
        <StatCard
          loading={isLoading}
          icon={<IconUsersGroup size={16} stroke={1.8} />}
          label="Total Members"
          value={data?.stats.totalMembers ?? 0}
          sub="Hub member records"
          tone="info"
        />
        <StatCard
          loading={isLoading}
          icon={<IconWorld size={16} stroke={1.8} />}
          label="Main Discord"
          value={data?.stats.membersInMainDiscord ?? 0}
          sub="Members in the main guild"
          tone="success"
        />
        <StatCard
          loading={isLoading}
          icon={<IconChartBar size={16} stroke={1.8} />}
          label="Active Members"
          value={data?.stats.activeMembers ?? 0}
          sub="Active or on LOA"
          tone="warning"
        />
        <StatCard
          loading={isLoading}
          icon={<IconUsers size={16} stroke={1.8} />}
          label="Online In Hub"
          value={data?.stats.onlineInHub ?? 0}
          sub="Recent active sessions"
          tone="info"
        />
        <StatCard
          loading={isLoading}
          icon={<IconDeviceGamepad2 size={16} stroke={1.8} />}
          label="On FiveM"
          value={data?.stats.activeFivem ?? 0}
          sub="Live sessions from worldwide-bot"
          tone="danger"
        />
        <StatCard
          loading={isLoading}
          icon={<IconMicrophone2 size={16} stroke={1.8} />}
          label="On TeamSpeak"
          value={data?.stats.activeTeamspeak ?? 0}
          sub="Live sessions from worldwide-bot"
          tone="warning"
        />
      </SimpleGrid>

      {/* ── Featured Bulletins ── */}
      {showSection && (
        <Card withBorder p="md" mb="md">
          <Group justify="space-between" mb={activeBulletins.length > 0 || isAdmin ? "md" : 0}>
            <Group gap="xs">
              <IconBell size={16} stroke={1.7} />
              <Title order={4}>Community Bulletins</Title>
            </Group>
            <Group gap="xs">
              {activeBulletins.length > 0 && (
                <Badge variant="light" color="blue">
                  {activeBulletins.length}
                </Badge>
              )}
              {isAdmin && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={13} />}
                  onClick={() => setCreatingBulletin(true)}
                >
                  New Bulletin
                </Button>
              )}
            </Group>
          </Group>

          {bulletinsLoading ? (
            <Stack gap="xs">
              <Skeleton height={72} radius="md" />
              <Skeleton height={72} radius="md" />
            </Stack>
          ) : bulletinsError ? (
            <Text size="sm" c="red">
              Failed to load bulletins.
            </Text>
          ) : activeBulletins.length === 0 && !isAdmin ? null : activeBulletins.length === 0 ? (
            <Text size="sm" c="dimmed">
              No active bulletins. Use &ldquo;New Bulletin&rdquo; to post one.
            </Text>
          ) : (
            <Stack gap="sm">
              {activeBulletins.map((b) => (
                <BulletinCard
                  key={b.id}
                  bulletin={b}
                  isAdmin={isAdmin}
                  onEdit={() => setEditingBulletin(b)}
                  onDelete={() => {
                    if (confirm("Delete this bulletin? This cannot be undone."))
                      deleteMutation.mutate(b.id);
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Expired bulletins visible to admins only */}
          {isAdmin && expiredBulletins.length > 0 && (
            <Stack gap="sm" mt="md">
              <Text
                size="xs"
                c="dimmed"
                fw={600}
                tt="uppercase"
                style={{ letterSpacing: "0.07em" }}
              >
                Expired
              </Text>
              {expiredBulletins.map((b) => (
                <BulletinCard
                  key={b.id}
                  bulletin={b}
                  isAdmin={isAdmin}
                  expired
                  onEdit={() => setEditingBulletin(b)}
                  onDelete={() => {
                    if (confirm("Delete this bulletin? This cannot be undone."))
                      deleteMutation.mutate(b.id);
                  }}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      {/* ── Who's Online ── */}
      <Card withBorder p="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconUsers size={16} stroke={1.7} />
            <Title order={4}>Who&apos;s Online</Title>
          </Group>
          <Badge variant="light" color="teal">
            {onlineMembers.length}
          </Badge>
        </Group>

        {onlineLoading ? (
          <Group justify="center" py="lg">
            <Loader size="sm" />
          </Group>
        ) : onlineMembers.length === 0 ? (
          <Text size="sm" c="dimmed">
            Nobody is currently online in the hub.
          </Text>
        ) : (
          <Group gap="xs" wrap="wrap">
            {onlineMembers.map((member) => (
              <OnlineMemberName
                key={member.id}
                color={roleColors[member.role] ?? FALLBACK_ROLE_COLOR}
                name={member.firstNameLastInitial ?? `Member #${member.id}`}
              />
            ))}
          </Group>
        )}
      </Card>

      {/* ── Modals ── */}
      {creatingBulletin && (
        <BulletinFormModal
          onClose={() => setCreatingBulletin(false)}
          onSuccess={() => {
            setCreatingBulletin(false);
            invalidate();
          }}
        />
      )}

      {editingBulletin && (
        <BulletinFormModal
          existing={editingBulletin}
          onClose={() => setEditingBulletin(null)}
          onSuccess={() => {
            setEditingBulletin(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  loading,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
  loading?: boolean;
  tone?: "danger" | "warning" | "success" | "info";
}) {
  return (
    <div className={`stat-card${tone ? ` stat-card--${tone}` : ""}`}>
      <div className="stat-card__label">
        {icon}
        {label}
      </div>
      {loading ? (
        <Skeleton height={34} width={90} radius="sm" />
      ) : (
        <div className="stat-card__value">{value}</div>
      )}
      <div className="stat-card__sub">{sub}</div>
    </div>
  );
}

function OnlineMemberName({ color, name }: { color: string; name: string }) {
  return (
    <Text size="sm" fw={600} c={color}>
      {name}
    </Text>
  );
}
